import { Suspense } from "react";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { ContributorAuthProvider } from "@/hooks/useContributorAuth";
import { MemberLayout, MemberSecurityBoundary } from "@/components/member/MemberLayout";
import { PortalErrorBoundary } from "@/components/PortalErrorBoundary";

// Member portal shell. Invite activation and password reset live OUTSIDE this
// layout (src/routes/member_/) so auth handshakes aren't interrupted.
export const Route = createFileRoute("/member")({
  component: MemberPortalLayout,
});

function MemberPortalLayout() {
  // Location advances before the old outlet commits. Only the committed leaf
  // may select an entry exception; routeIds also cover trailing-slash URLs.
  const isEntryRoute = useRouterState({
    select: state => {
      const leaf = state.matches[state.matches.length - 1];
      return leaf?.routeId === "/member/setup" || leaf?.routeId === "/member/login" ||
        // OAuth return: the user is mid sign-in and has no session until the
        // page exchanges the code, so the signed-in guard must not redirect it.
        leaf?.routeId === "/member/auth/callback" ||
        (leaf?.routeId === "/member/$" && leaf.status === "notFound");
    },
  });
  return (
    <ContributorAuthProvider>
      {/* Setup owns a captured signout/reauthentication transition and its own
          layout guard. Keep that exact route mounted throughout the handshake. */}
      {isEntryRoute ? <PortalErrorBoundary /> : (
        <MemberSecurityBoundary>
          <MemberLayout>
            <Suspense fallback={<LoadingIndicator label="Loading page" size="lg" className="min-h-[400px]" />}>
              <PortalErrorBoundary />
            </Suspense>
          </MemberLayout>
        </MemberSecurityBoundary>
      )}
    </ContributorAuthProvider>
  );
}
